import { ACCENT } from '../theme/tokens';
import {
  PANEL_PATHS,
  PANEL_VIEWBOX,
  RAIL_PATHS,
  RAIL_VIEWBOX,
} from './topography';

// The topographic contour texture — FD-487 (rail), FD-488 (sign-in panel).
//
// PURELY DECORATIVE. It carries no state, no meaning and no interaction: a
// viewer who cannot see it, or who has motion turned off, loses depth and
// nothing else. That is what makes it acceptable to place behind text at all,
// and it is why it is `aria-hidden` and `pointer-events-none` rather than
// merely unlabelled.
//
// WHY IT IS SAFE BEHIND TEXT, WITH THE ARITHMETIC RATHER THAN A REASSURANCE.
// A `#38BDF8` stroke at alpha a over `NAV.deep` composites to:
//
//     a = 0.18  ->  #0F3851   NAV.text 12.32:1   NAV.textMuted 6.97:1
//     a = 0.26  ->  #134660   NAV.text 10.17:1   NAV.textMuted  5.75:1
//     a = 0.36  ->  #185575   NAV.text  8.09:1   NAV.textMuted  4.58:1  <- ceiling
//
// The binding rung is `NAV.textMuted` (section headings, inactive nav labels,
// the sign-in body copy), not white. Past ~0.36 it drops below 4.5:1 and the
// rail's published 17.49 / 9.89 figures would stop being the worst case, which
// would mean a named exception in the token gate. Do not spend one on
// decoration: if a variant wants to be louder, that is the conversation.
//
// GEOMETRY lives in `topography.ts`, generated by
// `scripts/generate-topography.mjs`. COLOUR lives here, from tokens, so both
// surfaces re-tint from `theme/tokens.ts` like everything else.

export type TopographyVariant = 'rail' | 'panel';

interface VariantSpec {
  viewBox: string;
  paths: readonly string[];
  /** Stroke alpha where the fade mask is fully open. See the table above. */
  opacity: number;
  /**
   * `preserveAspectRatio`. Both SLICE rather than `meet`: `meet` would squash
   * the curves into ellipses on an off-aspect viewport, which reads as a
   * rendering fault rather than as terrain.
   */
  fit: string;
  /** The fade mask's gradient axis and midpoint, as `[x1,y1,x2,y2,mid]`. */
  fade: [string, string, string, string, string];
  /** Ambient motion. Only the sign-in panel has it — see the note below. */
  animated: boolean;
}

const VARIANTS: Record<TopographyVariant, VariantSpec> = {
  /**
   * The left rail.
   *
   * Anchored to the BOTTOM (`xMidYMax`) and faded from the top, because the
   * contours are densest low in the viewBox and that is where the rail has
   * empty space below the last nav item. Anchoring to the top would put the
   * busiest part of the drawing behind the brand and the nav list.
   *
   * NOT ANIMATED, deliberately. The rail is on screen on every route for the
   * whole session; ambient motion in an operator's peripheral vision all day is
   * a different proposition from motion on a screen they see for ten seconds.
   */
  rail: {
    viewBox: RAIL_VIEWBOX,
    paths: RAIL_PATHS,
    opacity: 0.18,
    fit: 'xMidYMax slice',
    fade: ['0', '0', '0', '1', '0.3'],
    animated: false,
  },
  /**
   * The sign-in column.
   *
   * Its own field rather than the rail's — see `generate-topography.mjs`. The
   * fade runs LEFT to right rather than top-down, because the column's neighbour
   * is the hero photograph, whose own scrim lands on `rgba(6,27,44,0.92)`: an
   * abrupt start to the contours at that seam would draw the eye to the join.
   * Nothing sits at the top of this column to fade away from.
   */
  panel: {
    viewBox: PANEL_VIEWBOX,
    paths: PANEL_PATHS,
    opacity: 0.26,
    fit: 'xMidYMid slice',
    fade: ['0', '0', '1', '0', '0.22'],
    animated: true,
  },
};

interface TopographyTextureProps {
  variant: TopographyVariant;
}

export default function TopographyTexture({ variant }: TopographyTextureProps) {
  const spec = VARIANTS[variant];
  // Per-variant ids. The two never render on the same page today — one is the
  // app shell, the other is the sign-in screen — but a duplicated `id` resolves
  // to whichever mounted first, and that is a bug that would appear only in
  // whatever future puts them together.
  const fadeId = `ab-topo-fade-${variant}`;
  const maskId = `ab-topo-mask-${variant}`;
  const [x1, y1, x2, y2, mid] = spec.fade;

  const svg = (
    <svg
      aria-hidden="true"
      focusable="false"
      className={`pointer-events-none absolute inset-0 h-full w-full${
        spec.animated ? ' ab-topo-drift' : ''
      }`}
      viewBox={spec.viewBox}
      preserveAspectRatio={spec.fit}
    >
      <defs>
        {/* SVG masks are LUMINANCE masks, so the gradient runs black (hidden)
            to white (shown) and the mid stop sets the curve's shape. */}
        <linearGradient id={fadeId} x1={x1} y1={y1} x2={x2} y2={y2}>
          <stop offset="0" stopColor="#000000" />
          <stop offset={mid} stopColor="#666666" />
          <stop offset="1" stopColor="#FFFFFF" />
        </linearGradient>
        <mask id={maskId}>
          <rect width="100%" height="100%" fill={`url(#${fadeId})`} />
        </mask>
      </defs>
      <g
        mask={`url(#${maskId})`}
        fill="none"
        stroke={ACCENT.highlight}
        strokeWidth={1}
        opacity={spec.opacity}
      >
        {spec.paths.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </svg>
  );

  if (!spec.animated) return svg;

  // AMBIENT MOTION — two transforms, on two elements, at two periods.
  //
  // The effect wanted is "wavy", and a single drift does not read that way: it
  // reads as sliding. What makes contour lines look like they are flexing is a
  // SHEAR, so the outer element skews while the inner one drifts. The periods
  // are 19s and 26s, which share no small factor, so the pair does not visibly
  // return to its start within any session — a single loop is the thing that
  // makes ambient motion start to feel mechanical.
  //
  // THE AMPLITUDE IS CALIBRATED IN PIXELS PER SECOND, AND THE FIRST ATTEMPT WAS
  // NOT (FD-489). It shipped at 31s/47s with a ~1.2 deg shear and a 2.5%
  // drift, which on a ~893px column works out at 0.48 px/s of drift and 0.43
  // px/s of shear — and the shear is zero at the vertical centre, which is
  // exactly where the form is and where the eye rests. The owner looked at the
  // deployed page twice and reported it static, correctly: casual perception of
  // uniform drift needs roughly 2 px/s, and this was under half of that at its
  // strongest point. The animation was running the whole time.
  //
  // So the numbers below are chosen from the arithmetic rather than from how
  // small they look in source:
  //
  //     drift X  6.4% of 893px = 57px over 26s = 2.2 px/s
  //     drift Y  3.2% of 1180px = 38px over 26s = 1.5 px/s
  //     shear    2.8 deg over 19s = 32px at the edges = 1.7 px/s
  //
  // "Subtle" is a rate, not a coefficient. If this ever wants retuning, retune
  // it in px/s against a real column width — an amplitude that reads as modest
  // in a keyframe can be invisible on screen, which is the whole lesson here.
  //
  // Both animate `transform` and nothing else, which is the cheap property:
  // composited, no layout, no repaint of the 54 stroked paths per frame.
  //
  // The keyframes carry `scale(1.14)` in EVERY frame rather than as a separate
  // static rule, because `transform` is one property and an animation on it
  // replaces whatever was there. The scale is not cosmetic: it is the bleed
  // margin. `slice` already overflows horizontally, but on a tall column the
  // vertical fit is exact, so a drift with no margin would walk the drawing's
  // own edge into view. The wrapper's negative inset does the same job for the
  // triangular gaps a skew opens at the corners.
  //
  // `prefers-reduced-motion` stops both and leaves the static texture — the
  // token contract's rule that motion is never the sole carrier of anything.
  return (
    <div
      aria-hidden="true"
      className="ab-topo-sway pointer-events-none absolute"
      style={{ inset: '-6%' }}
    >
      <style>{`
        .ab-topo-sway { animation: abTopoSway 19s ease-in-out infinite alternate; }
        .ab-topo-drift { animation: abTopoDrift 26s ease-in-out infinite alternate; }
        @keyframes abTopoSway {
          from { transform: skewX(-1.4deg) }
          to   { transform: skewX(1.4deg) }
        }
        @keyframes abTopoDrift {
          from { transform: scale(1.14) translate3d(-3.2%, -1.6%, 0) }
          to   { transform: scale(1.14) translate3d(3.2%, 1.6%, 0) }
        }
        @media (prefers-reduced-motion: reduce) {
          .ab-topo-sway, .ab-topo-drift { animation: none; }
        }
      `}</style>
      {svg}
    </div>
  );
}
